import { useCallback, useEffect, useState } from 'react';
import { GameShell, useGameClock, useReducedMotion } from './GameShell.js';

const COLS = 11;
const ROWS = 9;

interface Cell {
  x: number;
  y: number;
}

interface Pellet extends Cell {
  value: number;
  wanted: boolean;
}

/**
 * Snake, where the food is the next number.
 *
 * Chomp them in counting order - or in 2s, 5s and 10s once the child can skip
 * count, which the server decides from what they have mastered. The tail grows
 * with every right one, and a wrong one costs a segment rather than the game.
 *
 * A brand-new child with nothing mastered gets the same snake with nothing to
 * chase but itself, because a locked door where the fun is would be worse than
 * a plain game.
 */
export function QuokkaChomp({
  step,
  start,
  ceiling,
  seconds,
  onFinish,
}: {
  step: number;
  start: number;
  ceiling: number;
  seconds: number;
  onFinish: (score: number) => void;
}) {
  const reduced = useReducedMotion();
  const [body, setBody] = useState<Cell[]>([{ x: 5, y: 4 }]);
  const [heading, setHeading] = useState<Cell>({ x: 1, y: 0 });
  const [want, setWant] = useState(start);
  const [pellets, setPellets] = useState<Pellet[]>([]);
  const [eaten, setEaten] = useState(0);
  // Length is derived, not accumulated: three to start, one per correct
  // chomp, one back for a wrong one, never below two. Deriving it means the
  // tail can never drift out of step with the score on screen.
  const [penalties, setPenalties] = useState(0);
  const length = Math.max(2, 3 + eaten - penalties);

  const finish = useCallback(() => onFinish(eaten), [onFinish, eaten]);
  const left = useGameClock(seconds, finish);

  /** Scatter the wanted number plus two near-misses, never on the snake. */
  const scatter = useCallback(
    (target: number, occupied: Cell[]) => {
      const free = () => {
        for (let tries = 0; tries < 60; tries += 1) {
          const spot = {
            x: Math.floor(Math.random() * COLS),
            y: Math.floor(Math.random() * ROWS),
          };
          if (!occupied.some((c) => c.x === spot.x && c.y === spot.y)) return spot;
        }
        return { x: 0, y: 0 };
      };

      const decoys = new Set<number>();
      let guard = 0;
      while (decoys.size < 2 && guard++ < 30) {
        const near =
          target + step * (Math.random() < 0.5 ? 1 : -1) * (1 + Math.floor(Math.random() * 2));
        if (near > 0 && near <= ceiling && near !== target) decoys.add(near);
      }

      setPellets([
        { ...free(), value: target, wanted: true },
        ...[...decoys].map((value) => ({ ...free(), value, wanted: false })),
      ]);
    },
    [ceiling, step],
  );

  useEffect(() => {
    scatter(start, [{ x: 5, y: 4 }]);
  }, [scatter, start]);

  useEffect(() => {
    const tick = setInterval(
      () => {
        setBody((current) => {
          const head = current[0] as Cell;
          const next = {
            x: (head.x + heading.x + COLS) % COLS,
            y: (head.y + heading.y + ROWS) % ROWS,
          };

          const hit = pellets.find((p) => p.x === next.x && p.y === next.y);
          if (hit) {
            if (hit.wanted) {
              setEaten((n) => n + 1);
              const after = want + step > ceiling ? start : want + step;
              setWant(after);
              scatter(after, [next, ...current]);
            } else {
              // A wrong number costs a segment, never the run.
              setPenalties((n) => n + 1);
              setPellets((all) => all.filter((p) => p !== hit));
            }
          }

          return [next, ...current].slice(0, length);
        });
      },
      reduced ? 420 : 240,
    );
    return () => clearInterval(tick);
  }, [heading, pellets, want, step, ceiling, start, length, reduced, scatter]);

  useEffect(() => {
    const keys = (event: KeyboardEvent) => {
      const map: Record<string, Cell> = {
        ArrowUp: { x: 0, y: -1 },
        ArrowDown: { x: 0, y: 1 },
        ArrowLeft: { x: -1, y: 0 },
        ArrowRight: { x: 1, y: 0 },
      };
      const turn = map[event.key];
      if (turn) setHeading(turn);
    };
    window.addEventListener('keydown', keys);
    return () => window.removeEventListener('keydown', keys);
  }, []);

  const steer = (x: number, y: number) => setHeading({ x, y });

  return (
    <GameShell
      name="Quokka Chomp"
      scoreLabel="Chomped"
      score={eaten}
      secondsLeft={left}
      totalSeconds={seconds}
      onQuit={finish}
    >
      <p className="chomp__next">Next: {want}</p>
      <div
        className="chomp"
        style={{ gridTemplateColumns: `repeat(${COLS}, 1fr)` }}
        aria-label={`Chomp the number ${want}`}
      >
        {Array.from({ length: COLS * ROWS }, (_, i) => {
          const x = i % COLS;
          const y = Math.floor(i / COLS);
          const onSnake = body.some((c) => c.x === x && c.y === y);
          const isHead = body[0]?.x === x && body[0]?.y === y;
          const pellet = pellets.find((p) => p.x === x && p.y === y);
          return (
            <span
              key={i}
              className="chomp__cell"
              data-snake={onSnake || undefined}
              data-head={isHead || undefined}
            >
              {pellet ? <span className="chomp__pellet">{pellet.value}</span> : null}
            </span>
          );
        })}
      </div>

      <div className="chomp__pad">
        <button type="button" onClick={() => steer(0, -1)} aria-label="Up">
          ▲
        </button>
        <div>
          <button type="button" onClick={() => steer(-1, 0)} aria-label="Left">
            ◀
          </button>
          <button type="button" onClick={() => steer(1, 0)} aria-label="Right">
            ▶
          </button>
        </div>
        <button type="button" onClick={() => steer(0, 1)} aria-label="Down">
          ▼
        </button>
      </div>
    </GameShell>
  );
}
